import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  AlertTriangle,
  Check,
  Clipboard,
  Clock3,
  LoaderCircle,
  MailPlus,
  ShieldCheck,
  Stethoscope,
  UserRoundCheck,
  UserRoundMinus,
  UsersRound,
  X,
} from "lucide-react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  api,
  type CrmTeamInvitation,
  type CrmTeamInvitationCreated,
  type CrmTeamMember,
  type CrmTeamOverview,
  type MembershipRole,
  type ProWorkspaceType,
  type CrmOrganization,
} from "./api";
import { useAuth } from "./auth-context";
import { useCrm } from "./crm-context";

const ROLE_LABEL: Record<MembershipRole, string> = {
  OWNER: "Responsable",
  ADMIN: "Administración",
  VETERINARIAN: "Veterinario/a",
  ASSISTANT: "Asistencia",
  RECEPTION: "Recepción",
  AUDITOR: "Auditoría",
  EDITOR: "Edición",
  ANALYST: "Analítica",
};

const ROLE_DETAIL: Record<MembershipRole, string> = {
  OWNER: "Gobierno, equipo y operación completa",
  ADMIN: "Equipo y operación, sin transferir responsabilidad",
  VETERINARIAN: "Historia, firma clínica y agenda",
  ASSISTANT: "Historia y borradores, sin firma clínica",
  RECEPTION: "Agenda y admisión, sin historia clínica",
  AUDITOR: "Historia en lectura, sin escritura",
  EDITOR: "Edición operativa del workspace",
  ANALYST: "Métricas agregadas, sin escritura",
};

const ASSIGNABLE_ROLES: Array<Exclude<MembershipRole, "OWNER">> = [
  "ADMIN",
  "VETERINARIAN",
  "ASSISTANT",
  "RECEPTION",
  "AUDITOR",
];
const OPERATING_ROLES: Array<Exclude<MembershipRole, "OWNER">> = ["ADMIN", "EDITOR", "ANALYST"];
const INVITATION_STATUS = { PENDING: "Pendiente", ACCEPTED: "Aceptada", REVOKED: "Revocada", EXPIRED: "Vencida" };

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(new Date(value));
}

export function TeamScreen(): ReactNode {
  const { activeOrganization, activeWorkspace } = useCrm();
  if (!activeOrganization || !activeWorkspace) return <p role="status">Seleccioná una organización y un espacio de trabajo.</p>;
  // Remount the complete state (including private invite links) on context change.
  return <ScopedTeamScreen key={`${activeOrganization.id}:${activeWorkspace.type}`}
    activeOrganization={activeOrganization} workspace={activeWorkspace.type} />;
}

function ScopedTeamScreen({ activeOrganization, workspace }: { activeOrganization: CrmOrganization; workspace: ProWorkspaceType }): ReactNode {
  const organizationId = activeOrganization.id;
  const requestSequence = useRef(0);
  const [overview, setOverview] = useState<CrmTeamOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createdInvite, setCreatedInvite] = useState<CrmTeamInvitationCreated | null>(null);
  const [confirmMemberId, setConfirmMemberId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const roles = overview?.assignableRoles ?? (workspace === "VETERINARY" ? ASSIGNABLE_ROLES : OPERATING_ROLES)
    .filter((role) => overview?.viewerRole === "OWNER" || role !== "ADMIN");

  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError(null);
    try {
      const result = await api.team(organizationId, workspace);
      if (sequence === requestSequence.current) setOverview(result);
    } catch (reason) {
      if (sequence === requestSequence.current) {
        setOverview(null);
        setInviteOpen(false);
        setCreatedInvite(null);
        setError(reason instanceof Error ? reason.message : "No pudimos cargar el equipo.");
      }
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [organizationId, workspace]);

  useEffect(() => { void load(); return () => { requestSequence.current++; }; }, [load]);

  async function changeRole(member: CrmTeamMember, role: Exclude<MembershipRole, "OWNER">) {
    if (!activeOrganization || role === member.role) return;
    setPendingAction(member.id);
    setError(null);
    try {
      await api.updateTeamMemberRole(organizationId, member.id, role, workspace);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos cambiar el rol.");
    } finally {
      setPendingAction(null);
    }
  }

  async function revokeMember(member: CrmTeamMember) {
    if (!activeOrganization) return;
    setPendingAction(member.id);
    setError(null);
    try {
      await api.revokeTeamMember(organizationId, member.id, workspace);
      await load();
      setConfirmMemberId(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos revocar la membresía.");
    } finally {
      setPendingAction(null);
    }
  }

  async function revokeInvitation(invitation: CrmTeamInvitation) {
    if (!activeOrganization) return;
    setPendingAction(invitation.id);
    setError(null);
    try {
      await api.revokeTeamInvitation(organizationId, invitation.id, workspace);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos revocar la invitación.");
    } finally {
      setPendingAction(null);
    }
  }

  if (loading && !overview) {
    return (
      <section className="screen-loading" role="status">
        <LoaderCircle className="spin" size={22} aria-hidden="true" />
        Preparando el equipo…
      </section>
    );
  }

  return (
    <div className="screen team-screen">
      <header className="page-header">
        <div>
          <span className="eyebrow">Custodia organizacional</span>
          <h1>Equipo de {activeOrganization?.name}</h1>
          <p>Una identidad por persona, un rol explícito y cada cambio conservado en auditoría.</p>
        </div>
        {overview?.canManage && !inviteOpen && (
          <div className="page-header-action">
            <button
              className="button button-primary"
              type="button"
              onClick={() => {
                setCreatedInvite(null);
                setInviteOpen(true);
              }}
            >
              <MailPlus size={17} aria-hidden="true" /> Invitar persona
            </button>
          </div>
        )}
      </header>

      <section className="permission-rail" aria-labelledby="permission-rail-title">
        <div>
          <ShieldCheck size={18} aria-hidden="true" />
          <span>
            <strong id="permission-rail-title">Banda de custodia</strong>
            <small>Permisos visibles antes de delegar</small>
          </span>
        </div>
        {(["OWNER", ...(workspace === "VETERINARY" ? ASSIGNABLE_ROLES : OPERATING_ROLES)] as MembershipRole[]).map((role) => (
          <span className={`permission-chip permission-${role.toLowerCase()}`} key={role}>
            {ROLE_LABEL[role]}
          </span>
        ))}
      </section>

      {error && (
        <div className="error-panel" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <div>
            <strong>No pudimos completar la acción</strong>
            <p>{error}</p>
          </div>
          <button className="button-link" type="button" onClick={() => void load()}>
            Actualizar
          </button>
        </div>
      )}

      {inviteOpen && activeOrganization && overview && (
        <TeamInviteComposer
          organizationId={activeOrganization.id}
          workspace={workspace}
          roles={roles}
          created={createdInvite}
          onCreated={(invite) => {
            setCreatedInvite(invite);
            setOverview((current) =>
              current
                ? {
                    ...current,
                    invitations: [
                      {
                        id: invite.id,
                        email: invite.email,
                        role: invite.role,
                        expiresAt: invite.expiresAt,
                        createdAt: new Date().toISOString(),
                        status: "PENDING",
                        canRevoke: true,
                      },
                      ...current.invitations,
                    ],
                  }
                : current,
            );
          }}
          onClose={() => {
            setInviteOpen(false);
            setCreatedInvite(null);
          }}
        />
      )}

      <div className="team-layout">
        <section className="panel team-roster" aria-labelledby="team-roster-title">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Accesos e historial</span>
              <h2 id="team-roster-title">Personas del equipo</h2>
            </div>
            <span className="count-badge">{overview?.members.length ?? 0}</span>
          </div>
          <div className="team-member-list">
            {overview && overview.members.length === 0 && <p className="muted">Todavía no hay integrantes en este espacio.</p>}
            {overview?.members.map((member) => {
              const confirming = confirmMemberId === member.id;
              const busy = pendingAction === member.id;
              return (
                <article className="team-member" key={member.id}>
                  <span className="team-avatar" aria-hidden="true">
                    {member.displayName.charAt(0).toUpperCase()}
                  </span>
                  <div className="team-member-identity">
                    <div>
                      <h3>{member.displayName}</h3>
                      {member.role === "OWNER" && (
                        <span className="status status-active">Custodia principal</span>
                      )}
                    </div>
                    <p>{member.email}</p>
                    <small>{member.status === "REVOKED" ? "Acceso retirado" : member.accountStatus && member.accountStatus !== "ACTIVE" ? "Cuenta suspendida o no activa" : member.status === "INVITED" ? "Invitación pendiente" : "Acceso activo"}</small>
                    <small>{ROLE_DETAIL[member.role]}</small>
                    {!member.canManage && member.role !== "OWNER" && !member.workspaceScopes?.length && workspace !== "VETERINARY" && <small>Membresía organizacional previa: no se modifica desde un único rubro.</small>}
                  </div>
                  <div className="team-member-role">
                    {member.canManage ? (
                      <label>
                        <span>Rol</span>
                        <select
                          value={member.role}
                          disabled={busy}
                          onChange={(event) =>
                            void changeRole(
                              member,
                              event.target.value as Exclude<MembershipRole, "OWNER">,
                            )
                          }
                        >
                          {roles.map((role) => (
                            <option key={role} value={role}>
                              {ROLE_LABEL[role]}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : (
                      <span className="role-label">{ROLE_LABEL[member.role]}</span>
                    )}
                  </div>
                  {member.canManage && (
                    <div className="team-member-actions">
                      {confirming ? (
                        <div
                          className="inline-confirm"
                          role="group"
                          aria-label="Confirmar revocación"
                        >
                          <span>¿Revocar acceso?</span>
                          <button
                            className="button-link danger-link"
                            type="button"
                            disabled={busy}
                            onClick={() => void revokeMember(member)}
                          >
                            {busy ? "Revocando…" : "Sí, revocar"}
                          </button>
                          <button
                            className="button-link"
                            type="button"
                            disabled={busy}
                            onClick={() => setConfirmMemberId(null)}
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <button
                          className="icon-button"
                          type="button"
                          onClick={() => setConfirmMemberId(member.id)}
                          aria-label={`Revocar acceso de ${member.displayName}`}
                        >
                          <UserRoundMinus size={16} />
                        </button>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>

        <aside className="team-side">
          <section className="panel" aria-labelledby="pending-invitations-title">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Acción pendiente</span>
                <h2 id="pending-invitations-title">Invitaciones</h2>
              </div>
              <span className="count-badge">{overview?.invitations.length ?? 0}</span>
            </div>
            {overview?.canManage ? (
              overview.invitations.length ? (
                <ul className="team-invitation-list">
                  {overview.invitations.map((invitation) => (
                    <li key={invitation.id}>
                      <Clock3 size={16} aria-hidden="true" />
                      <span>
                        <strong>{invitation.email}</strong>
                        <small>
                          {ROLE_LABEL[invitation.role]} · {INVITATION_STATUS[invitation.status ?? "PENDING"]} · vence {formatDate(invitation.expiresAt)}
                        </small>
                      </span>
                      {invitation.canRevoke !== false && <button
                        className="icon-button"
                        type="button"
                        disabled={pendingAction === invitation.id}
                        onClick={() => void revokeInvitation(invitation)}
                        aria-label={`Revocar invitación de ${invitation.email}`}
                      >
                        {pendingAction === invitation.id ? (
                          <LoaderCircle className="spin" size={15} />
                        ) : (
                          <X size={15} />
                        )}
                      </button>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No hay invitaciones pendientes.</p>
              )
            ) : (
              <p className="muted">Solo responsables y administración ven invitaciones.</p>
            )}
          </section>
          <section className="team-trust-note">
            <Stethoscope size={19} aria-hidden="true" />
            <div>
              <strong>{workspace === "VETERINARY" ? "Matrícula antes de firmar" : "Acceso limitado a este espacio"}</strong>
              <p>{workspace === "VETERINARY" ? "El rol veterinario solo se activa con perfil profesional aprobado." : "La invitación no habilita clínica ni otros rubros. Retirar acceso tiene efecto incluso con una sesión abierta."}</p>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}

function TeamInviteComposer({
  organizationId,
  workspace,
  roles,
  created,
  onCreated,
  onClose,
}: {
  organizationId: string;
  workspace: ProWorkspaceType;
  roles: Array<Exclude<MembershipRole, "OWNER">>;
  created: CrmTeamInvitationCreated | null;
  onCreated: (invite: CrmTeamInvitationCreated) => void;
  onClose: () => void;
}): ReactNode {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Exclude<MembershipRole, "OWNER">>(workspace === "VETERINARY" ? "VETERINARIAN" : "EDITOR");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const inviteUrl = created
    ? `${window.location.origin}/invitaciones/equipo#token=${encodeURIComponent(created.acceptToken)}`
    : "";

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      onCreated(await api.inviteTeamMember(organizationId, { email, role }, workspace));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos crear la invitación.");
    } finally {
      setSubmitting(false);
    }
  }

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
    } catch {
      setError("No pudimos copiar automáticamente. Seleccioná el enlace y copialo.");
    }
  }

  return (
    <section className="team-invite-panel" aria-labelledby="team-invite-title">
      <div className="clinical-document-tray-heading">
        <div>
          <span className="eyebrow">Acceso de un solo uso</span>
          <h2 id="team-invite-title">{created ? "Invitación preparada" : "Invitar al equipo"}</h2>
        </div>
        <button
          className="icon-button"
          type="button"
          onClick={onClose}
          aria-label="Cerrar invitación"
        >
          <X size={17} />
        </button>
      </div>
      {created ? (
        <div className="invite-success" role="status">
          <span className="invite-success-icon" aria-hidden="true">
            <Check size={19} />
          </span>
          <div>
            <strong>{created.email}</strong>
            <p>
              El enlace vence el {formatDate(created.expiresAt)} y solo funciona con ese correo.
            </p>
            <label>
              <span>Enlace privado</span>
              <input value={inviteUrl} readOnly onFocus={(event) => event.target.select()} />
            </label>
            <button
              className="button button-secondary button-compact"
              type="button"
              onClick={() => void copy()}
            >
              {copied ? <Check size={16} /> : <Clipboard size={16} />}
              {copied ? "Enlace copiado" : "Copiar enlace"}
            </button>
          </div>
        </div>
      ) : (
        <form className="team-invite-form" onSubmit={(event) => void submit(event)}>
          <label>
            <span>Correo de la persona</span>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="persona@ejemplo.com"
              required
              autoFocus
            />
          </label>
          <label>
            <span>Rol inicial</span>
            <select
              value={role}
              onChange={(event) => setRole(event.target.value as Exclude<MembershipRole, "OWNER">)}
            >
              {roles.map((item) => (
                <option key={item} value={item}>
                  {ROLE_LABEL[item]} — {ROLE_DETAIL[item]}
                </option>
              ))}
            </select>
          </label>
          <p className="form-note">
            El token se muestra una sola vez. Hasta conectar email transaccional, compartilo por un
            canal privado.
          </p>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <div className="form-actions">
            <button
              className="button button-secondary button-compact"
              type="button"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              className="button button-primary button-compact"
              type="submit"
              disabled={submitting}
            >
              {submitting && <LoaderCircle className="spin" size={15} />}
              {submitting ? "Preparando…" : "Crear invitación"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

export function TeamInvitationAcceptScreen(): ReactNode {
  const { status } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.hash.slice(1)).get("token") ?? searchParams.get("token") ?? "";
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState<{
    organizationName: string;
    role: MembershipRole;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (status === "loading") {
    return (
      <main className="invitation-page" role="status">
        <LoaderCircle className="spin" size={24} />
        Verificando tu sesión…
      </main>
    );
  }
  if (status === "anonymous") {
    return (
      <Navigate to="/ingresar" replace state={{ from: `${location.pathname}${location.search}${location.hash}` }} />
    );
  }

  async function accept(): Promise<void> {
    if (!token) {
      setError("El enlace no contiene un token de invitación válido.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.acceptTeamInvitation(token);
      setAccepted({ organizationName: result.organizationName, role: result.role });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos aceptar la invitación.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="invitation-page">
      <section className="invitation-card">
        <span className="invitation-mark" aria-hidden="true">
          {accepted ? <UserRoundCheck size={30} /> : <UsersRound size={30} />}
        </span>
        <span className="eyebrow">Tappet CRM · acceso protegido</span>
        <h1>{accepted ? "Ya sos parte del equipo" : "Tenés una invitación de equipo"}</h1>
        {accepted ? (
          <>
            <p>
              Tu acceso a <strong>{accepted.organizationName}</strong> quedó activo como{" "}
              <strong>{ROLE_LABEL[accepted.role]}</strong>.
            </p>
            <button
              className="button button-primary"
              type="button"
              onClick={() => void navigate("/", { replace: true })}
            >
              Entrar al CRM
            </button>
          </>
        ) : (
          <>
            <p>
              Confirmá únicamente si reconocés a la organización que te compartió este enlace. La
              invitación queda ligada al correo de tu cuenta.
            </p>
            {error && (
              <div className="document-error" role="alert">
                <AlertTriangle size={18} aria-hidden="true" />
                <p>{error}</p>
              </div>
            )}
            <button
              className="button button-primary"
              type="button"
              onClick={() => void accept()}
              disabled={submitting}
            >
              {submitting ? <LoaderCircle className="spin" size={17} /> : <ShieldCheck size={17} />}
              {submitting ? "Confirmando…" : "Aceptar invitación"}
            </button>
          </>
        )}
      </section>
    </main>
  );
}
